/**
 * Lectura y validación de la lista de dominios corporativos que se teclea en «Fuente de datos».
 *
 * Vive aparte del componente a propósito: es la parte que puede equivocarse de verdad, y así se
 * prueba sin renderizar nada.
 *
 * Lo que esta lista decide: quien tiene correo de uno de estos dominios **entra por SSO y no puede
 * auto-registrarse**; quien no, se registra. O sea que un dominio de más cierra el auto-registro a su
 * gente, y uno de menos lo abre. Por eso el flujo no es «valida y guarda», sino **«valida, enséñale
 * lo que va a guardar, y luego guarda»**.
 */

/** Un dominio que no se pudo aceptar, con el valor tal cual lo escribió la persona. */
export interface ProblemaDominio {
  tipo: 'formato' | 'duplicado';
  valor: string;
}

export interface DominiosLeidos {
  /** Normalizados: minúsculas, sin espacios alrededor, sin repetidos, en el orden en que se escribieron. */
  dominios: string[];
  problemas: ProblemaDominio[];
}

/**
 * Un dominio de verdad: etiquetas separadas por puntos, sin espacios, y un TLD alfabético.
 *
 * Se exige el punto porque un token sin él (`sigma`, o la mitad de un dominio partido) no es un
 * dominio y aceptarlo dejaría una entrada que nunca empata con nada — silenciosamente.
 */
const DOMINIO = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

/**
 * Separa por **comas, puntos y coma, saltos de línea y tabuladores** — lo que la gente teclea o pega.
 *
 * 🔴 **No** separa por espacios, y es deliberado. Si separara, `sigma alimentos.com` se convertiría en
 * `sigma` (que se descarta por no tener punto) y `alimentos.com` (que **sí** es válido) — o sea que un
 * dominio mal escrito entraría como otro dominio distinto y nadie se enteraría. Sin separar por
 * espacios, ese texto sale marcado como error de formato, que es exactamente lo que hay que ver.
 */
export function leerDominios(texto: string): DominiosLeidos {
  const dominios: string[] = [];
  const problemas: ProblemaDominio[] = [];
  const vistos = new Set<string>();

  for (const bruto of (texto ?? '').split(/[,;\n\r\t]+/)) {
    const token = bruto.trim();
    if (token === '') continue;   // comas de más, líneas vacías: no son un error, son formato humano

    const normalizado = token.toLowerCase();
    if (!DOMINIO.test(normalizado)) {
      problemas.push({ tipo: 'formato', valor: token });
      continue;
    }
    if (vistos.has(normalizado)) {
      problemas.push({ tipo: 'duplicado', valor: token });
      continue;
    }
    vistos.add(normalizado);
    dominios.push(normalizado);
  }

  return { dominios, problemas };
}

export interface CambioDominios {
  anadidos: string[];
  quitados: string[];
}

/**
 * Qué cambia respecto a lo que hay guardado.
 *
 * 🔴 Es la protección que de verdad importa, y no una validación: **ninguna regla de formato puede
 * detectar que a una lista pegada le falta una coma**. `campofriofg.comcaroli.ro` es un dominio
 * sintácticamente impecable. Lo que sí lo delata es verlo escrito en «se va a añadir», junto a los dos
 * que desaparecieron de la lista.
 *
 * Se intentó una heurística —marcar un TLD conocido en medio del token— y se descartó: `company.com`
 * la disparaba. Una advertencia que cría lobos deja de leerse, y aquí lo que se lee mal se guarda.
 */
export function compararDominios(
  antes: readonly string[],
  despues: readonly string[],
): CambioDominios {
  const previos = new Set(antes.map((d) => d.toLowerCase()));
  const nuevos = new Set(despues.map((d) => d.toLowerCase()));

  return {
    anadidos: [...nuevos].filter((d) => !previos.has(d)).sort(),
    quitados: [...previos].filter((d) => !nuevos.has(d)).sort(),
  };
}

/** El texto para el `textarea`: uno por línea, que es como se lee y se edita. */
export function aTexto(dominios: readonly string[]): string {
  return [...dominios].join('\n');
}
